import React, { createContext } from "react";
import {
  CognitoUser,
  AuthenticationDetails
} from "amazon-cognito-identity-js";

import Pool from "./UserPool";

const AccountContext = createContext();

const Account = (props) => {
  const getSession = async (forceRefresh = false) => {
    return new Promise((resolve, reject) => {
      const user = Pool.getCurrentUser();

      if (!user) {
        reject(new Error("No user found"));
        return;
      }

      user.getSession((sessionError, session) => {
        if (sessionError || !session) {
          reject(
            sessionError ||
            new Error("Could not receive Cognito session")
          );
          return;
        }

        const finishSession = async (actualSession) => {
          if (!actualSession || !actualSession.isValid()) {
            reject(new Error("Session is invalid"));
            return;
          }

          try {
            const accessToken = actualSession
              .getAccessToken()
              .getJwtToken();

            /*
             * Завжди зберігаємо актуальний токен.
             * Старе значення localStorage більше не залишається
             * лежати після Cognito refresh.
             */
            localStorage.setItem("token", accessToken);

            const attributes = await new Promise(
              (resolveAttributes, rejectAttributes) => {
                user.getUserAttributes(
                  (attributesError, attributesList) => {
                    if (attributesError) {
                      rejectAttributes(attributesError);
                      return;
                    }

                    const result = {};

                    for (const attribute of attributesList || []) {
                      result[attribute.Name] = attribute.Value;
                    }

                    resolveAttributes(result);
                  }
                );
              }
            );

            resolve({
              user,
              session: actualSession,
              accessToken,
              ...attributes
            });
          } catch (error) {
            reject(error);
          }
        };

        /*
         * Звичайний сценарій:
         * getSession сам оновлює протухлу сесію через refresh token.
         */
        if (!forceRefresh) {
          finishSession(session);
          return;
        }

        /*
         * Після 401 примусово оновлюємо Cognito-сесію,
         * навіть якщо локально вона ще виглядає валідною.
         */
        const refreshToken = session.getRefreshToken();

        if (!refreshToken) {
          reject(new Error("Refresh token is missing"));
          return;
        }

        user.refreshSession(
          refreshToken,
          (refreshError, refreshedSession) => {
            if (refreshError || !refreshedSession) {
              reject(
                refreshError ||
                new Error("Could not refresh Cognito session")
              );
              return;
            }

            finishSession(refreshedSession);
          }
        );
      });
    });
  };

  const authenticate = async (Username, Password) => {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username,
        Pool
      });

      const authDetails = new AuthenticationDetails({
        Username,
        Password
      });

      user.authenticateUser(authDetails, {
        onSuccess: (data) => {
          const accessToken = data
            .getAccessToken()
            .getJwtToken();

          localStorage.setItem("token", accessToken);

          resolve(data);
        },

        onFailure: (error) => {
          reject(error);
        },

        newPasswordRequired: (data) => {
          resolve(data);
        }
      });
    });
  };

  const logout = () => {
    const user = Pool.getCurrentUser();

    if (user) {
      user.signOut();
    }

    localStorage.removeItem("token");

    return Promise.resolve();
  };

  return (
    <AccountContext.Provider
      value={{
        authenticate,
        getSession,
        logout
      }}
    >
      {props.children}
    </AccountContext.Provider>
  );
};

export {
  Account,
  AccountContext
};